'use client';

import { useTranslation } from 'react-i18next';

interface LanguageToggleProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Shared language-toggle button used on the dashboard, login, and public
 * booking pages. Switching language:
 *  1. Calls i18n.changeLanguage() so all translations update live.
 *  2. Sets document.documentElement.dir + .lang so dir flips globally.
 *  3. Persists the choice to localStorage ('slikerr_locale') so a page
 *     reload restores the selected language via providers.tsx LocaleRestorer.
 */
export function LanguageToggle({ className, style }: LanguageToggleProps) {
  const { i18n, t } = useTranslation();

  const toggle = () => {
    const next = i18n.language === 'he' ? 'en' : 'he';
    void i18n.changeLanguage(next);
    if (typeof window !== 'undefined') {
      document.documentElement.dir = next === 'he' ? 'rtl' : 'ltr';
      document.documentElement.lang = next;
      localStorage.setItem('slikerr_locale', next);
    }
  };

  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={toggle}
      aria-label={t('common.toggle_language')}
      data-testid="language-toggle"
    >
      {t('common.language')}
    </button>
  );
}
